import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Journal d'activité (audit) d'un utilisateur.
// Trace les événements clés d'un compte pour la console SuperAdmin :
// connexions, création de compte, changements de type de compte,
// et actions applicatives diverses.
// Écriture + lecture BEST-EFFORT : l'échec du log ne doit jamais
// bloquer l'action métier d'origine.
// ─────────────────────────────────────────────────────────────

export type AuditCategory = 'login' | 'type_change' | 'account' | 'action';

export interface UserAuditEntry {
  id: number;
  userId: number;
  category: string;
  action: string;
  description: string;
  actorId: number | null;
  metadata: Record<string, unknown> | null;
  createdAt: string | null;
}

export interface LogUserAuditInput {
  userId: number | null | undefined;
  category: AuditCategory | string;
  action?: string;
  description?: string;
  actorId?: number | null;
  metadata?: Record<string, unknown> | null;
}

/** Enregistre une entrée d'audit pour un utilisateur (best-effort). */
export async function logUserAudit(input: LogUserAuditInput): Promise<void> {
  const userId = Number(input.userId);
  if (!Number.isFinite(userId) || userId <= 0) return;
  try {
    await supabase.from('user_audit_log').insert({
      user_id: userId,
      category: input.category,
      action: input.action || input.category,
      description: (input.description || '').slice(0, 400),
      actor_id: input.actorId ?? null,
      metadata: input.metadata ?? null,
    });
  } catch {
    /* audit best-effort */
  }
}

/** Historique d'audit d'un utilisateur, du plus récent au plus ancien. */
export async function fetchUserAudit(userId: number, limit = 120): Promise<UserAuditEntry[]> {
  if (!Number.isFinite(userId) || userId <= 0) return [];
  try {
    const { data, error } = await supabase
      .from('user_audit_log')
      .select('id, user_id, category, action, description, actor_id, metadata, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((r) => ({
      id: Number(r.id),
      userId: Number(r.user_id),
      category: String(r.category || 'action'),
      action: String(r.action || ''),
      description: String(r.description || ''),
      actorId: r.actor_id == null ? null : Number(r.actor_id),
      metadata: (r.metadata as Record<string, unknown>) ?? null,
      createdAt: r.created_at ? String(r.created_at) : null,
    }));
  } catch {
    return [];
  }
}

export const AUDIT_CATEGORY_META: Record<string, { label: string; icon: string; className: string }> = {
  login: { label: 'Connexion', icon: 'ri-login-box-line', className: 'text-primary-600 bg-primary-50' },
  type_change: { label: 'Type de compte', icon: 'ri-shield-user-line', className: 'text-accent-700 bg-accent-50' },
  account: { label: 'Compte', icon: 'ri-user-add-line', className: 'text-secondary-600 bg-secondary-50' },
  action: { label: 'Action', icon: 'ri-flashlight-line', className: 'text-foreground-600 bg-background-100' },
};

export function auditCategoryMeta(category: string) {
  return AUDIT_CATEGORY_META[category] || AUDIT_CATEGORY_META.action;
}